import { useParams } from 'react-router-dom';
import { systemClock } from '@sakhtar/calendar';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import type { CustomerSalesReportResponse } from '../../lib/types/crm.js';
import { baseOrganizationPayloadOf, setupStepView, type SetupView } from '../../lib/types/setup.js';
import { formatRialFa } from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import { isReady, PrintLoadState, PrintPageShell, TenantIdentityBlock } from './PrintChrome.js';

/**
 * گزارش فروش به مشتریان — print view of the real by-customer sales report
 * (`GET .../crm/reports/customers`, `aggregateCustomerSalesReport`). The
 * design's own "گزارش فروش دوره‌ای" board shows a date-ranged period
 * (١٤٠٤/٠٧/٠١ تا ١٤٠٤/٠٧/٣٠); the real endpoint has no date filter at all
 * (`listOrdersForReport` has no WHERE clause on any date column), so this
 * is deliberately titled and labelled as an all-time snapshot rather than
 * showing invented period boundaries — a KPI-card redesign with fabricated
 * receivables-aging/trend figures (the design's other three cards) is
 * likewise not rendered; only the real customer/order-count/total table the
 * backend actually returns.
 *
 * UI_REFERENCE_SOURCE: Sakhtar Print Documents.dc.html, page 2
 *   ("گزارش فروش دوره‌ای"). status: PARTIAL_REAL — customer breakdown table
 *   structure follows the design; period framing, KPI cards, and
 *   receivables-aging figures are omitted (no backend support).
 */
export function CustomerSalesReportPrintPage() {
  const { unit, label, ready } = useAmountDisplay();
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const report = useApiGet<CustomerSalesReportResponse>(
    slug ? `${base}/api/v1/crm/reports/customers` : null,
    [slug],
  );
  const setup = useApiGet<SetupView>(slug ? `${base}/api/v1/setup` : null, [slug]);

  if (!slug) return null;

  const backHref = `${base}/crm/customers`;
  if (!ready) {
    return (
      <PrintPageShell backHref={backHref} backLabel="بازگشت به مشتریان">
        <PrintLoadState />
      </PrintPageShell>
    );
  }

  if (!isReady(report)) {
    return (
      <PrintPageShell backHref={backHref} backLabel="بازگشت به مشتریان">
        <PrintLoadState error={report.status === 'error' ? report.error : undefined} />
      </PrintPageShell>
    );
  }

  const baseOrg = isReady(setup)
    ? baseOrganizationPayloadOf(setupStepView(setup.data, 'base-organization'))
    : null;
  const today = systemClock.today();
  const rows = report.data.customers;
  const totalOrders = rows.reduce((sum, row) => sum + row.orderCount, 0);
  const totalRial = rows.reduce((sum, row) => sum + BigInt(row.totalRial), 0n).toString();

  return (
    <PrintPageShell backHref={backHref} backLabel="بازگشت به مشتریان">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontSize: 12.5 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24 }}>
          <TenantIdentityBlock
            legalIdentity={baseOrg?.legalIdentity ?? null}
            primaryLocation={baseOrg?.primaryLocation ?? null}
            personType={baseOrg?.personType ?? null}
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              alignItems: 'flex-end',
              textAlign: 'left',
            }}
          >
            <span style={{ fontSize: 18, fontWeight: 700 }}>گزارش فروش به مشتریان</span>
            <span style={{ color: '#555' }}>
              مبنا: مجموع سفارش‌های ثبت‌شده تاکنون (بدون بازهٔ زمانی)
            </span>
            <span>تاریخ تهیه {displayJalaliDate(today)}</span>
          </div>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #333' }}>
              <th style={{ textAlign: 'right', padding: '6px 4px' }}>مشتری</th>
              <th style={{ textAlign: 'right', padding: '6px 4px' }}>کد</th>
              <th style={{ textAlign: 'right', padding: '6px 4px' }}>تعداد سفارش</th>
              <th style={{ textAlign: 'left', padding: '6px 4px' }}>فروش خالص ({label})</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ padding: '14px 4px', color: '#777' }}>
                  سفارشی برای گزارش‌گیری ثبت نشده است.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.partyRef} style={{ borderBottom: '1px solid #e2e2e2' }}>
                  <td style={{ padding: '6px 4px' }}>{row.partyName ?? row.partyRef}</td>
                  <td
                    className="ltr sk-num"
                    style={{ padding: '6px 4px', fontFamily: 'var(--sk-font-mono)' }}
                  >
                    {row.partyRef}
                  </td>
                  <td style={{ padding: '6px 4px' }}>{row.orderCount.toLocaleString('fa-IR')}</td>
                  <td className="ltr sk-num" style={{ padding: '6px 4px', textAlign: 'left' }}>
                    {formatRialFa(row.totalRial, unit)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {rows.length > 0 ? (
            <tfoot>
              <tr style={{ borderTop: '2px solid #333', fontWeight: 700 }}>
                <td style={{ padding: '6px 4px' }} colSpan={2}>
                  جمع کل
                </td>
                <td style={{ padding: '6px 4px' }}>{totalOrders.toLocaleString('fa-IR')}</td>
                <td className="ltr sk-num" style={{ padding: '6px 4px', textAlign: 'left' }}>
                  {formatRialFa(totalRial, unit)}
                </td>
              </tr>
            </tfoot>
          ) : null}
        </table>

        <p style={{ fontSize: 11, color: '#666', lineHeight: 1.9, margin: 0 }}>
          گزارش داخلی — تا ۵۰ مشتری برتر بر مبنای فروش، سفارش‌های لغوشده حذف شده‌اند. واحد پول:{' '}
          {label}.
        </p>
      </div>
    </PrintPageShell>
  );
}
